import { useCallback, useMemo } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import {
  REQUEST_KIND,
  type EvidenceRequestDetail,
  type EvidenceRequestLineItem,
} from '../../../evidence-management/evidence/evidenceTypes';
import { personnelLabel, usePersonnelNames } from '../../../human-resources/personnel/useStaff';

/**
 * What one request asks for — a receiving request's items as the requester described them, or a
 * transfer request's stored items.
 *
 * **Its own file, because the profile guards with an early return** — `useDataTable` on the page would
 * be a conditional hook. **Searched, sorted and paged IN MEMORY**: the lines arrive WHOLE with the
 * record (the requisition slip's exemption), so the filter sees everything there is.
 */
export function EvidenceRequestItemsPanel({ request }: { request: EvidenceRequestDetail }) {
  const isTransfer = request.kind === REQUEST_KIND.transfer;

  // MEMOISED — an unmemoised source changes `fetchPage`'s identity every render and loops the table.
  const rows = useMemo(() => request.lines, [request]);

  const acquirers = useMemo(() => rows.map((l) => l.acquiredByPersonnelId), [rows]);
  const { names, canRead } = usePersonnelNames(acquirers);

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((line) =>
            [line.inventoryNumber, line.typeName, line.description, line.containerNumber]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      // A COPY, never an in-place sort; the wire form is `field:asc` — `useDataTable`'s own spelling.
      const [field, direction] = (query.sort ?? '').split(':');
      const descending = direction === 'desc';

      const sorted = field
        ? [...matched].sort((a, b) => compare(a, b, field) * (descending ? -1 : 1))
        : matched;

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({ items: sorted.slice(start, start + query.pageSize), total: sorted.length });
    },
    [rows],
  );

  const state = useDataTable<EvidenceRequestLineItem>({ fetchPage, pageSize: 50 });

  const columns = useMemo<DataColumn<EvidenceRequestLineItem>[]>(
    () =>
      isTransfer
        ? [
            {
              key: 'inventoryNumber',
              header: 'Inventory No.',
              width: 170,
              sortable: true,
              render: (row) => <TextCell value={row.inventoryNumber} mono />,
            },
            { key: 'typeName', header: 'Type', width: 150, sortable: true, render: (row) => <TextCell value={row.typeName} /> },
            {
              key: 'description',
              header: 'Description',
              width: 340,
              render: (row) => <TextCell value={row.description} />,
            },
            {
              key: 'containerNumber',
              header: 'Container/Box No',
              width: 150,
              render: (row) => <TextCell value={row.containerNumber} mono />,
            },
          ]
        : [
            { key: 'typeName', header: 'Type', width: 150, sortable: true, render: (row) => <TextCell value={row.typeName} /> },
            {
              key: 'description',
              header: 'Description',
              width: 320,
              render: (row) => <TextCell value={row.description} />,
            },
            {
              key: 'acquiredAt',
              header: 'Acquired',
              width: 190,
              sortable: true,
              render: (row) => <DateCell value={row.acquiredAt ?? null} />,
            },
            {
              key: 'acquiredBy',
              header: 'Acquired by',
              width: 190,
              render: (row) => <TextCell value={personnelLabel(row.acquiredByPersonnelId, names, canRead)} />,
            },
            {
              // A many-valued cell joined on one line — the documents filed with this item.
              key: 'attachments',
              header: 'Attachments',
              width: 220,
              render: (row) => <TextCell value={row.attachments.map((a) => a.fileName).join(', ') || null} />,
            },
          ],
    [isTransfer, names, canRead],
  );

  return (
    <DataTablePanel<EvidenceRequestLineItem>
      id="evidence-request-items"
      columns={columns}
      rowKey={(line) => line.id}
      state={state}
      fill
      // Nothing can be done to a line of a request once it is raised — read-only, so no selection.
      selectable={false}
      searchPlaceholder={isTransfer ? 'Search inventory no., type or description' : 'Search type or description'}
      emptyMessage="This request names no items."
      emptySearchMessage="No item matches that search."
    />
  );
}

function compare(a: EvidenceRequestLineItem, b: EvidenceRequestLineItem, field: string): number {
  const value = (line: EvidenceRequestLineItem): string => {
    switch (field) {
      case 'inventoryNumber':
        return line.inventoryNumber ?? '';
      case 'typeName':
        return line.typeName ?? '';
      case 'acquiredAt':
        return line.acquiredAt ?? '';
      default:
        return String(line.lineNumber).padStart(6, '0');
    }
  };

  return value(a).localeCompare(value(b));
}
